import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ElementType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import {
  deleteInstagramConversation,
  getInstagramConversation,
  getInstagramMessages,
  getInstagramUserProfile,
} from '../../../hooks/instagram';
import {
  instagramAccountDetails,
  InstagramMessage,
} from '../../../utils/types';
import ChatSidebar from './InstagramConversations';
import Composer from '../components/Composer';
import InstagramConvWindowMenu from '../components/InstagramConversationMenu';
import {
  formatInstagramDay,
  formatInstagramTime,
  getInstagramMessageStatus,
  InstagramDateSeparator,
  InstagramInboxShell,
  InstagramMessageBubble,
  InstagramThreadHeader,
  getInstagramMessageAttachments,
  igAccent,
} from '../components/instagramInboxUi';

type MessageGroup = {
  dateLabel: string;
  messages: InstagramMessage[];
};

const buildDateGroups = (messages: InstagramMessage[]): MessageGroup[] => {
  const groups = new Map<string, InstagramMessage[]>();

  messages.forEach((message) => {
    const key = message.timestamp || '';
    const label = formatInstagramDay(key) || 'Unknown date';
    const existing = groups.get(label) || [];
    existing.push(message);
    groups.set(label, existing);
  });

  return Array.from(groups.entries()).map(([dateLabel, groupedMessages]) => ({
    dateLabel,
    messages: groupedMessages,
  }));
};

export default function InstagramConversationDetailSmall() {
  const { orgId, convId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [messages, setMessages] = useState<InstagramMessage[]>([]);
  const [conversation, setConversation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [instagramAccDetails, setInstagramAccDetails] =
    useState<instagramAccountDetails | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const groupedMessages = useMemo(() => buildDateGroups(messages), [messages]);

  const fetchConversation = async () => {
    if (!orgId || !convId) return;

    setLoading(true);
    setError(null);
    try {
      const [conversationRes, messagesRes, accountRes] = await Promise.all([
        getInstagramConversation(orgId, convId),
        getInstagramMessages(orgId, convId),
        getInstagramUserProfile(orgId),
      ]);

      if (conversationRes.success) {
        setConversation(conversationRes.data);
      } else {
        throw new Error('Conversation not found');
      }

      if (messagesRes.success) {
        setMessages(Array.isArray(messagesRes.data?.data) ? messagesRes.data.data : []);
      }

      if (accountRes.success && Array.isArray(accountRes.data) && accountRes.data[0]) {
        setInstagramAccDetails(accountRes.data[0]);
      }
    } catch (fetchError) {
      setError('Failed to load this conversation.');
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 80);
    }
  };

  useEffect(() => {
    fetchConversation();
  }, [orgId, convId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (message: InstagramMessage) => {
    setMessages((prev) => [...prev, message]);
  };

  const handleDeleteConversation = async () => {
    if (!orgId || !convId) return;

    onClose();
    try {
      const response = await deleteInstagramConversation(orgId, convId);
      if (!response.success) {
        throw new Error('Delete failed');
      }

      toast({
        title: 'Conversation deleted',
        description: 'The Instagram thread has been cleared.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate(`/organization/${orgId}/inbox/instagram/conversations`);
    } catch (deleteError) {
      toast({
        title: 'Deletion failed',
        description: 'Could not delete this conversation.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <InstagramInboxShell
      hideSidebarOnMobile
      sidebar={orgId ? <ChatSidebar orgId={orgId} activeConversationId={convId} /> : null}
      thread={
        <Flex direction="column" h="100%">
          <InstagramThreadHeader
            title={conversation?.participant_username || 'Instagram user'}
            subtitle={
              conversation?.participant_ig_id
                ? `Instagram conversation • ${conversation.participant_ig_id}`
                : 'Instagram conversation'
            }
            avatarName={conversation?.participant_username || 'Instagram user'}
            avatarSrc={conversation?.profile_picture || undefined}
            onBack={() => navigate(`/organization/${orgId}/inbox/instagram/conversations`)}
            actions={
              orgId &&
              convId &&
              instagramAccDetails && (
                <HStack spacing={1}>
                  <Tooltip label="Settings">
                    <IconButton
                      aria-label="Instagram settings"
                      icon={<Icon as={Settings as ElementType} boxSize={4} />}
                      variant="ghost"
                      borderRadius="full"
                      onClick={() =>
                        navigate(
                          `/organization/${orgId}/inbox/instagram/${instagramAccDetails.id}/settings`,
                        )
                      }
                    />
                  </Tooltip>
                  <InstagramConvWindowMenu
                    orgId={orgId}
                    onDeleteOpen={onOpen}
                    instagramAccountId={instagramAccDetails.id}
                  />
                </HStack>
              )
            }
          />

          {error ? (
            <Box p={6}>
              <Alert status="error" borderRadius="20px">
                <AlertIcon />
                <Box>
                  <AlertTitle>Conversation unavailable</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Box>
              </Alert>
            </Box>
          ) : null}

          <Box flex="1" overflowY="auto" px={{ base: 4, md: 8 }} py={5}>
            {loading ? (
              <Flex h="100%" align="center" justify="center">
                <Spinner color={igAccent} size="lg" />
              </Flex>
            ) : null}

            {!loading && !messages.length ? (
              <Flex h="100%" align="center" justify="center">
                <VStack spacing={2} textAlign="center">
                  <Text fontWeight="700" color="gray.900">
                    No messages yet
                  </Text>
                  <Text fontSize="sm" color="gray.500" maxW="320px">
                    This thread is connected and ready. New Instagram messages
                    will appear here as they sync.
                  </Text>
                </VStack>
              </Flex>
            ) : null}

            {!loading &&
              groupedMessages.map((group) => (
                <Box key={group.dateLabel}>
                  <InstagramDateSeparator label={group.dateLabel} />
                  <VStack spacing={2.5} align="stretch">
                    {group.messages.map((message) => {
                      const outgoing = message.direction === 'outbound';
                      const status = getInstagramMessageStatus(message);
                      const attachments = getInstagramMessageAttachments(message);
                      const isEdited = Boolean(message.raw_payload?.message_edit);
                      const meta = outgoing
                        ? `${formatInstagramTime(message.timestamp)}${status ? ` • ${status}` : ''}`
                        : formatInstagramTime(message.timestamp);
                      const messageBody = message.text || (attachments.length ? '' : 'Unsupported message');

                      return (
                        <Flex
                          key={message.id}
                          justify={outgoing ? 'flex-end' : 'flex-start'}
                        >
                          <InstagramMessageBubble
                            outgoing={outgoing}
                            failed={status === 'failed'}
                            attachments={attachments}
                            meta={isEdited ? `${meta} • edited` : meta}
                          >
                            {messageBody}
                          </InstagramMessageBubble>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              ))}
            <Box ref={messagesEndRef} />
          </Box>

          <Box
            px={{ base: 3, md: 5 }}
            py={3}
            borderTop="1px solid"
            borderColor="rgba(15, 23, 42, 0.08)"
            bg="rgba(255,255,255,0.92)"
            backdropFilter="blur(12px)"
          >
            {conversation && orgId && convId ? (
              <Composer
                conversationId={convId}
                orgId={orgId}
                recipient_phone={conversation.instagram_account_id || ''}
                onSent={handleSendMessage}
              />
            ) : null}
          </Box>

          {isOpen ? (
            <Box
              position="fixed"
              inset={0}
              bg="rgba(15, 23, 42, 0.42)"
              zIndex={20}
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={4}
            >
              <Box
                w="full"
                maxW="420px"
                bg="white"
                borderRadius="24px"
                p={6}
                boxShadow="0 24px 64px rgba(15,23,42,0.22)"
              >
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="800" fontSize="lg" color="gray.900">
                      Delete conversation?
                    </Text>
                    <Text mt={2} color="gray.500" fontSize="sm" lineHeight="1.7">
                      This clears the current Instagram thread from your CRM
                      inbox. This action cannot be undone.
                    </Text>
                  </Box>
                  <HStack justify="flex-end">
                    <Button variant="ghost" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button colorScheme="red" onClick={handleDeleteConversation}>
                      Delete
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Box>
          ) : null}
        </Flex>
      }
    />
  );
}
